import { logger } from '../utils/logger';
import apiClient from './api';
import { API_BASE_URL } from '../config/apiConfig';

// Types
export interface AIConversation {
  id: number;
  user_id: number;
  project_id?: number;
  title: string;
  context_type: 'general' | 'project' | 'technical' | 'estimation';
  system_prompt: string;
  metadata?: Record<string, any>;
  is_archived: boolean;
  last_message_at: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  project?: {
    id: number;
    name: string;
    description: string;
  };
}

export interface AIMessage {
  id: number;
  conversation_id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: Record<string, any>;
  model_used?: string;
  tokens_used?: number;
  processing_time_ms?: number;
  created_at: string;
  updated_at: string;
}

export interface ConversationWithMessages extends AIConversation {
  messages: AIMessage[];
}

export interface ConversationStats {
  total_messages: number;
  user_messages: number;
  assistant_messages: number;
  total_tokens: number;
  avg_response_time_ms: number;
  created_at: string;
  last_message_at: string;
}

export interface CreateConversationRequest {
  title: string;
  project_id?: number;
  context_type?: 'general' | 'project' | 'technical' | 'estimation';
  custom_system_prompt?: string;
}

export interface SendMessageRequest {
  message: string;
}

export interface SendMessageResponse {
  user_message: AIMessage;
  assistant_message: AIMessage;
  processing_time_ms: number;
}

class AIConsoleService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  /**
   * List all conversations for the current user
   */
  async listConversations(projectId?: number, includeArchived: boolean = false): Promise<AIConversation[]> {
    try {
      logger.info('Fetching AI conversations', { projectId, includeArchived });

      const params = new URLSearchParams();
      if (projectId) params.append('project_id', projectId.toString());
      if (includeArchived) params.append('include_archived', 'true');

      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetch(`${API_BASE_URL}/ai-console/conversations${queryString}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch conversations');
      }

      logger.info('AI conversations fetched successfully', { count: data.data.length });

      return data.data;
    } catch (error) {
      logger.error('Failed to fetch AI conversations', { error });
      throw error;
    }
  }

  /**
   * Create a new conversation
   */
  async createConversation(request: CreateConversationRequest): Promise<ConversationWithMessages> {
    try {
      logger.info('Creating AI conversation', { title: request.title, projectId: request.project_id });

      const response = await fetch(`${API_BASE_URL}/ai-console/conversations`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to create conversation');
      }

      logger.info('AI conversation created successfully', { conversationId: data.data.id });

      return data.data;
    } catch (error) {
      logger.error('Failed to create AI conversation', { error });
      throw error;
    }
  }

  /**
   * Get a specific conversation with all messages
   */
  async getConversation(conversationId: number): Promise<{ conversation: ConversationWithMessages; stats: ConversationStats }> {
    try {
      logger.info('Fetching AI conversation', { conversationId });

      const response = await fetch(`${API_BASE_URL}/ai-console/conversations/${conversationId}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch conversation');
      }

      logger.info('AI conversation fetched successfully', { conversationId, messageCount: data.data.conversation.messages.length });

      return data.data;
    } catch (error) {
      logger.error('Failed to fetch AI conversation', { error, conversationId });
      throw error;
    }
  }

  /**
   * Send a message in a conversation
   */
  async sendMessage(conversationId: number, message: string): Promise<SendMessageResponse> {
    try {
      logger.info('Sending message to AI', { conversationId, messageLength: message.length });

      const response = await fetch(`${API_BASE_URL}/ai-console/conversations/${conversationId}/message`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ message }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to send message');
      }

      logger.info('Message sent successfully', {
        conversationId,
        processingTime: data.data.processing_time_ms
      });

      return data.data;
    } catch (error) {
      logger.error('Failed to send message to AI', { error, conversationId });
      throw error;
    }
  }

  /**
   * Archive a conversation
   */
  async archiveConversation(conversationId: number): Promise<void> {
    try {
      logger.info('Archiving AI conversation', { conversationId });

      const response = await fetch(`${API_BASE_URL}/ai-console/conversations/${conversationId}/archive`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to archive conversation');
      }

      logger.info('AI conversation archived successfully', { conversationId });
    } catch (error) {
      logger.error('Failed to archive AI conversation', { error, conversationId });
      throw error;
    }
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: number): Promise<void> {
    try {
      logger.info('Deleting AI conversation', { conversationId });

      const response = await fetch(`${API_BASE_URL}/ai-console/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to delete conversation');
      }

      logger.info('AI conversation deleted successfully', { conversationId });
    } catch (error) {
      logger.error('Failed to delete AI conversation', { error, conversationId });
      throw error;
    }
  }

  /**
   * Get all conversations for a specific project
   */
  async getProjectConversations(projectId: number): Promise<{ project: any; conversations: AIConversation[] }> {
    try {
      logger.info('Fetching project AI conversations', { projectId });

      const response = await fetch(`${API_BASE_URL}/ai-console/projects/${projectId}/conversations`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch project conversations');
      }

      logger.info('Project AI conversations fetched successfully', {
        projectId,
        count: data.data.conversations.length
      });

      return data.data;
    } catch (error) {
      logger.error('Failed to fetch project AI conversations', { error, projectId });
      throw error;
    }
  }

  /**
   * Stream a message (for future implementation with streaming responses)
   */
  async streamMessage(conversationId: number, message: string, onChunk: (chunk: string) => void): Promise<void> {
    // TODO: Implement streaming when backend supports it
    // For now, use regular sendMessage
    const response = await this.sendMessage(conversationId, message);
    onChunk(response.assistant_message.content);
  }
}

export const aiConsoleService = new AIConsoleService();
export default aiConsoleService;
